<template>
  <main class="tracking-worksheet-page bg-grey-1">
    <section
      v-if="worksheetEntry && worksheet"
      class="tracking-worksheet-content q-pa-md"
    >
      <q-banner
        v-if="sourceLoadError"
        dense
        class="bg-red-1 text-red-10 q-mb-md"
      >
        {{ sourceLoadError }} Passage status remains current; source and time
        details can be retried.
        <template #action>
          <q-btn
            flat
            dense
            round
            icon="refresh"
            aria-label="Retry verified passage source load"
            @click="loadVerifiedPassageSources(true)"
          >
            <q-tooltip>Retry source details</q-tooltip>
          </q-btn>
        </template>
      </q-banner>

      <q-table
        v-model:pagination="pagination"
        :rows="worksheet.rows"
        :columns="worksheetColumns"
        :visible-columns="visibleWorksheetColumns"
        :filter="filter"
        row-key="pipelineSectionMarkerId"
        dense
        flat
        bordered
        square
        :rows-per-page-options="[25, 50, 0]"
        class="tracking-worksheet-table bg-white"
        @row-click="onWorksheetRowClick"
      >
        <template #top>
          <div class="row full-width items-center q-col-gutter-md">
            <div class="col-12 row items-start no-wrap">
              <div class="col">
                <div class="text-subtitle1 text-weight-bold text-grey-9">
                  {{ worksheet.summary.runName }}
                </div>
                <div class="text-caption text-grey-7">
                  {{ worksheet.summary.verifiedMarkerCount }} of
                  {{ worksheet.summary.markerCount }} markers verified
                </div>
              </div>
              <q-btn
                flat
                dense
                no-caps
                class="q-ml-sm"
                color="primary"
                icon="file_download"
                label="Export CSV"
                aria-label="Export tracksheet CSV"
                @click="downloadCsv"
              >
                <q-tooltip>Export tracksheet CSV</q-tooltip>
              </q-btn>
            </div>

            <div v-if="isEndedRun" class="col-12 worksheet-completed-metrics">
              <div class="row items-center q-col-gutter-md">
                <div class="col-auto">
                  <span class="worksheet-metric-label">Run</span>
                  <span class="worksheet-metric-value">{{
                    worksheet.summary.lifecycleState === 'X'
                      ? 'Cancelled'
                      : 'Completed'
                  }}</span>
                </div>
                <div v-if="worksheet.summary.pipelineName" class="col-auto">
                  <span class="worksheet-metric-label">Pipeline</span>
                  <span
                    class="worksheet-metric-value worksheet-metric-value--wrap"
                  >
                    {{ worksheet.summary.pipelineName }}
                  </span>
                </div>
              </div>

              <div class="row items-center q-col-gutter-md q-mt-sm">
                <div v-if="worksheet.summary.launchTimeUtc" class="col-auto">
                  <span class="worksheet-metric-label">Launch (UTC)</span>
                  <span class="worksheet-metric-value">
                    {{ formatUtc(worksheet.summary.launchTimeUtc) }}
                  </span>
                </div>
                <div v-if="worksheet.summary.receiveTimeUtc" class="col-auto">
                  <span class="worksheet-metric-label">Receive (UTC)</span>
                  <span class="worksheet-metric-value">
                    {{ formatUtc(worksheet.summary.receiveTimeUtc) }}
                  </span>
                </div>
              </div>

              <div class="row items-center q-col-gutter-md q-mt-sm">
                <div
                  v-if="worksheet.summary.deviceCount !== null"
                  class="col-auto"
                >
                  <span class="worksheet-metric-label">Devices</span>
                  <span class="worksheet-metric-value">
                    {{ worksheet.summary.deviceCount }}
                  </span>
                </div>
                <div
                  v-if="worksheet.summary.passageCount !== null"
                  class="col-auto"
                >
                  <span class="worksheet-metric-label">Passages</span>
                  <span class="worksheet-metric-value">
                    {{ worksheet.summary.passageCount }}
                  </span>
                </div>
                <div
                  v-if="worksheet.summary.verifiedPassageCount !== null"
                  class="col-auto"
                >
                  <span class="worksheet-metric-label">Verified passages</span>
                  <span class="worksheet-metric-value">
                    {{ worksheet.summary.verifiedPassageCount }}
                  </span>
                </div>
              </div>
            </div>

            <div
              v-else
              class="col-12 row items-center q-col-gutter-md worksheet-metrics"
            >
              <div v-if="worksheet.summary.nextMarkerName" class="col-auto">
                <span class="worksheet-metric-label">Next</span>
                <span class="worksheet-metric-value">
                  {{ worksheet.summary.nextMarkerName }}
                </span>
              </div>
              <div
                v-if="worksheet.summary.currentSpeedMetersPerSecond !== null"
                class="col-auto"
              >
                <span class="worksheet-metric-label">Speed</span>
                <span class="worksheet-metric-value">
                  {{
                    formatPrimarySpeed(
                      worksheet.summary.currentSpeedMetersPerSecond
                    )
                  }}
                  {{ primarySpeedUnitSymbol }}
                </span>
              </div>
              <div v-if="worksheet.summary.nextMarkerEtaUtc" class="col-auto">
                <span class="worksheet-metric-label">Next ETA (UTC)</span>
                <span class="worksheet-metric-value">
                  {{ formatUtc(worksheet.summary.nextMarkerEtaUtc) }}
                </span>
              </div>
              <div
                v-if="isOverdue(worksheet.summary.nextMarkerOverdueSeconds)"
                class="col-auto"
              >
                <span class="worksheet-metric-label">Awaiting passage</span>
                <span class="worksheet-metric-value text-negative">
                  overdue by
                  {{
                    formatCountdown(worksheet.summary.nextMarkerOverdueSeconds)
                  }}
                </span>
              </div>
            </div>

            <div class="col-12 row items-center no-wrap q-gutter-x-sm">
              <q-spinner-dots
                v-if="sourceLoadState === 'loading'"
                color="primary"
                size="22px"
              >
                <q-tooltip>Loading selected passage sources</q-tooltip>
              </q-spinner-dots>
              <q-select
                v-model="visibleColumns"
                multiple
                borderless
                dense
                options-dense
                emit-value
                map-options
                :display-value="$q.lang.table.columns"
                :options="worksheetColumns"
                option-value="name"
                style="min-width: 50px"
                @popup-hide="activeWorksheetColumnVariantMenu = null"
              >
                <template #option="scope">
                  <q-item
                    v-bind="scope.itemProps"
                    class="tracking-worksheet-column-option"
                    :class="worksheetColumnDragClass(scope.opt.name)"
                    draggable="true"
                    @dragstart="
                      startWorksheetColumnDrag($event, scope.opt.name)
                    "
                    @dragover="
                      onWorksheetColumnDragOver(
                        $event,
                        scope.opt.name,
                        'vertical'
                      )
                    "
                    @drop="
                      onWorksheetColumnDrop($event, scope.opt.name, 'vertical')
                    "
                    @dragend="endWorksheetColumnDrag"
                  >
                    <q-item-section>
                      <q-item-label>{{ scope.opt.label }}</q-item-label>
                    </q-item-section>
                    <q-item-section
                      v-if="hasWorksheetColumnVariants(scope.opt.name)"
                      side
                    >
                      <q-btn
                        flat
                        dense
                        round
                        size="sm"
                        icon="chevron_right"
                        class="tracking-worksheet-column-option__variant-button"
                        :aria-label="`Choose column variant for ${scope.opt.label}`"
                        @pointerdown.stop
                        @click.stop="
                          openWorksheetColumnVariantMenu(scope.opt.name)
                        "
                      >
                        <q-tooltip>Choose column variant</q-tooltip>
                        <q-menu
                          :model-value="
                            activeWorksheetColumnVariantMenu === scope.opt.name
                          "
                          anchor="top end"
                          self="top start"
                          :offset="[4, 0]"
                          no-parent-event
                          @update:model-value="
                            setWorksheetColumnVariantMenuOpen(
                              scope.opt.name,
                              $event
                            )
                          "
                        >
                          <q-list
                            dense
                            class="tracking-worksheet-column-variant-menu"
                          >
                            <q-item
                              v-for="variant in worksheetColumnVariantOptions(
                                scope.opt.name
                              )"
                              :key="variant.value"
                              dense
                              clickable
                              @click.stop="
                                setWorksheetColumnVariant(
                                  scope.opt.name,
                                  variant.value
                                )
                              "
                            >
                              <q-item-section>
                                {{
                                  worksheetColumnVariantLabel(
                                    scope.opt.name,
                                    variant.value
                                  )
                                }}
                              </q-item-section>
                              <q-item-section side>
                                <q-icon
                                  v-if="
                                    worksheetColumnVariant(scope.opt.name) ===
                                    variant.value
                                  "
                                  name="check"
                                  color="primary"
                                />
                              </q-item-section>
                            </q-item>
                          </q-list>
                        </q-menu>
                      </q-btn>
                    </q-item-section>
                  </q-item>
                </template>
                <template #after-options>
                  <q-separator />
                  <q-item
                    dense
                    clickable
                    class="tracking-worksheet-column-order-reset"
                    :disable="isWorksheetColumnOrderDefault()"
                    @click.stop="resetWorksheetColumnOrder"
                  >
                    <q-item-section avatar>
                      <q-icon name="restart_alt" />
                    </q-item-section>
                    <q-item-section>Reset column order</q-item-section>
                  </q-item>
                </template>
              </q-select>
              <q-input
                v-model="filter"
                dense
                debounce="300"
                color="primary"
                aria-label="Filter tracksheet markers"
              >
                <template #append>
                  <q-icon name="search" />
                </template>
              </q-input>
            </div>
          </div>
        </template>

        <template #header-cell="props">
          <q-th
            :props="props"
            class="tracking-worksheet-column-header"
            :class="worksheetColumnDragClass(props.col.name)"
            draggable="true"
            @dragstart="startWorksheetColumnDrag($event, props.col.name)"
            @dragover="
              onWorksheetColumnDragOver($event, props.col.name, 'horizontal')
            "
            @drop="onWorksheetColumnDrop($event, props.col.name, 'horizontal')"
            @dragend="endWorksheetColumnDrag"
          >
            {{ props.col.label }}
          </q-th>
        </template>

        <template #body-cell-marker="props">
          <q-td :props="props">
            <div class="row items-start no-wrap">
              <q-btn
                v-if="
                  props.row.latitude !== null && props.row.longitude !== null
                "
                flat
                dense
                round
                size="sm"
                icon="map"
                class="q-mr-xs q-mt-xs"
                :aria-label="`Show ${props.row.markerName} on map`"
                @click.stop="showMarkerOnMap(props.row)"
              >
                <q-tooltip>Show on map</q-tooltip>
              </q-btn>
              <div>
                <div class="text-weight-medium">{{ props.row.markerName }}</div>
                <div
                  v-if="props.row.description"
                  class="text-caption text-grey-7 worksheet-description"
                >
                  {{ props.row.description }}
                </div>
              </div>
            </div>
          </q-td>
        </template>

        <template #body-cell-status="props">
          <q-td :props="props">
            <q-badge :color="rowStatusColor(props.row)" text-color="white">
              {{ rowStatusLabel(props.row) }}
            </q-badge>
          </q-td>
        </template>
      </q-table>
    </section>

    <section
      v-else-if="completedRunLoadState === 'loading'"
      class="column items-center justify-center q-pa-xl text-center tracking-worksheet-empty"
    >
      <q-spinner-dots color="primary" size="40px" />
      <div class="text-body1 q-mt-md text-grey-8">
        Loading completed tracksheet...
      </div>
    </section>

    <section
      v-else
      class="column items-center justify-center q-pa-xl text-center tracking-worksheet-empty"
    >
      <q-icon name="route" color="grey-5" size="48px" />
      <div class="text-body1 q-mt-md text-grey-8">
        {{ completedRunLoadError || 'No pig run selected.' }}
      </div>
      <div class="text-caption q-mt-xs text-grey-6">
        Select an active or completed run to view its tracksheet.
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import type { QTableColumn } from 'quasar';
import { PigRunMapEntry } from '@/models/PigRunState';
import { PigRunSummaryData } from '@/models/PigRunSummaryData';
import {
  VerifiedPassageSource,
  VerifiedPassageSourcesLoadState,
} from '@/models/VerifiedPassageSource';
import {
  TrackingWorksheet,
  TrackingWorksheetColumnIds,
  TrackingWorksheetPassSource,
  TrackingWorksheetRow,
  TrackingWorksheetRowStatus,
  formatTrackingWorksheetTrackState,
} from '@/models/TrackingWorksheet';
import PigRunService from '@/services/PigRunService';
import {
  buildTrackingWorksheet,
  isEndedLifecycleState,
  parseUtcDateTime,
} from '@/services/TrackingWorksheetBuilder';
import { downloadTrackingWorksheetCsv } from '@/services/TrackingWorksheetCsvExport';

const VISIBLE_COLUMNS_STORAGE_KEY = 'trackingWorksheetVisibleColumns';
const COLUMN_ORDER_STORAGE_KEY = 'trackingWorksheetColumnOrder';
const COLUMN_UNIT_STORAGE_KEY = 'trackingWorksheetColumnUnits';
const COLUMN_VARIANT_STORAGE_KEY = 'trackingWorksheetColumnVariants';
const LEGACY_DISTANCE_UNIT_STORAGE_KEY = 'trackingWorksheetDistanceUnit';
const LEGACY_SPEED_UNIT_STORAGE_KEY = 'trackingWorksheetSpeedUnit';
const METERS_PER_FOOT = 0.3048;
const METERS_PER_MILE = 1609.344;
const MILES_PER_HOUR_PER_METER_PER_SECOND = 2.2369362920544;
const KILOMETERS_PER_HOUR_PER_METER_PER_SECOND = 3.6;
const ACTIVE_DEFAULT_VISIBLE_COLUMNS = [
  TrackingWorksheetColumnIds.marker,
  TrackingWorksheetColumnIds.status,
  TrackingWorksheetColumnIds.passSource,
  TrackingWorksheetColumnIds.passUtc,
  TrackingWorksheetColumnIds.etaUtc,
  TrackingWorksheetColumnIds.countdown,
  TrackingWorksheetColumnIds.legSpeed,
  TrackingWorksheetColumnIds.trackState,
];

const COMPLETED_DEFAULT_VISIBLE_COLUMNS = [
  TrackingWorksheetColumnIds.marker,
  TrackingWorksheetColumnIds.status,
  TrackingWorksheetColumnIds.passSource,
  TrackingWorksheetColumnIds.passUtc,
  TrackingWorksheetColumnIds.legSpeed,
  TrackingWorksheetColumnIds.trackState,
];

type CompletedRunLoadState = 'idle' | 'loading' | 'loaded' | 'error';
type ColumnDropDirection = 'horizontal' | 'vertical';
type ColumnDropPosition = 'before' | 'after';
type TrackingWorksheetDistanceUnit = 'meters' | 'feet' | 'miles';
type TrackingWorksheetSpeedUnit =
  | 'metersPerSecond'
  | 'milesPerHour'
  | 'kilometersPerHour';
type TrackingWorksheetTimeZone = 'utc' | 'local';

const DISTANCE_UNIT_OPTIONS: {
  label: string;
  value: TrackingWorksheetDistanceUnit;
}[] = [
  { label: 'Meters (m)', value: 'meters' },
  { label: 'Feet (ft)', value: 'feet' },
  { label: 'Miles (mi)', value: 'miles' },
];

const SPEED_UNIT_OPTIONS: {
  label: string;
  value: TrackingWorksheetSpeedUnit;
}[] = [
  { label: 'Miles per hour (mph)', value: 'milesPerHour' },
  { label: 'Meters per second (m/s)', value: 'metersPerSecond' },
  { label: 'Kilometers per hour (km/h)', value: 'kilometersPerHour' },
];

const TIME_ZONE_OPTIONS: {
  label: string;
  value: TrackingWorksheetTimeZone;
}[] = [
  { label: 'UTC', value: 'utc' },
  { label: 'Local', value: 'local' },
];

const DISTANCE_COLUMN_IDS = [
  TrackingWorksheetColumnIds.legDistance,
  TrackingWorksheetColumnIds.distanceFromLaunch,
] as const;

const SPEED_COLUMN_IDS = [
  TrackingWorksheetColumnIds.legSpeed,
  TrackingWorksheetColumnIds.runAverageSpeed,
] as const;

const TIME_COLUMN_IDS = [
  TrackingWorksheetColumnIds.passUtc,
  TrackingWorksheetColumnIds.etaUtc,
] as const;

function isTrackingWorksheetDistanceUnit(
  value: unknown
): value is TrackingWorksheetDistanceUnit {
  return DISTANCE_UNIT_OPTIONS.some((unit) => unit.value === value);
}

function isTrackingWorksheetSpeedUnit(
  value: unknown
): value is TrackingWorksheetSpeedUnit {
  return SPEED_UNIT_OPTIONS.some((unit) => unit.value === value);
}

function isTrackingWorksheetTimeZone(
  value: unknown
): value is TrackingWorksheetTimeZone {
  return TIME_ZONE_OPTIONS.some((option) => option.value === value);
}

function buildCompletedPigRunEntry(
  pigRun: PigRunSummaryData,
  verifiedPassageSources: VerifiedPassageSource[],
  verifiedPassageSourcesLoadState: VerifiedPassageSourcesLoadState,
  verifiedPassageSourcesError: string | null
): PigRunMapEntry {
  return {
    pigRun: {
      pigRunId: pigRun.pigRunId,
      pipelineSectionConfigurationId: 0,
      type: pigRun.type,
      lifecycleState: pigRun.lifecycleState,
      name: pigRun.name,
      launchDate: pigRun.launchDate ?? '',
      jobNumber: pigRun.jobNumber ?? '',
      expectedSpeed: 0,
      isArchived: pigRun.isArchived,
      markers: [],
      devices: pigRun.devices,
      tools: [],
    },
    markers: pigRun.markers.map((marker) => ({
      pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
      name: marker.name,
      trackState: marker.trackState,
      description: marker.description,
      pigRunId: pigRun.pigRunId,
      latitude: marker.latitude,
      longitude: marker.longitude,
      pipelineDistance: marker.pipelineDistance,
      sortOrder: marker.sortOrder,
      hasVerifiedPassage: marker.hasVerifiedPassage,
      manualLinkDevices: [],
    })),
    pigLocation: null,
    verifiedPassageSources,
    verifiedPassageSourcesLoadState,
    verifiedPassageSourcesError,
  };
}

function isValidDate(date: Date | null): date is Date {
  return date !== null && !Number.isNaN(date.getTime());
}

export default defineComponent({
  name: 'PigRunTrackingWorksheet',
  props: {
    entry: {
      type: Object as PropType<PigRunMapEntry | null>,
      required: false,
      default: null,
    },
    completedPigRunId: {
      type: Number as PropType<number | null>,
      default: null,
    },
  },
  emits: ['show-marker-on-map'],
  data() {
    return {
      clock: new Date(),
      clockInterval: null as ReturnType<typeof setInterval> | null,
      completedPigRun: null as PigRunSummaryData | null,
      completedRunLoadState: 'idle' as CompletedRunLoadState,
      completedRunLoadError: null as string | null,
      completedVerifiedPassageSources: [] as VerifiedPassageSource[],
      completedSourceLoadState: 'idle' as VerifiedPassageSourcesLoadState,
      completedSourceLoadError: null as string | null,
      completedRunRequestVersion: 0,
      filter: '',
      pagination: {
        rowsPerPage: 25,
      },
      visibleColumns: [...ACTIVE_DEFAULT_VISIBLE_COLUMNS] as string[],
      distanceColumnUnits: {
        [TrackingWorksheetColumnIds.legDistance]: 'meters',
        [TrackingWorksheetColumnIds.distanceFromLaunch]: 'meters',
      } as Record<string, TrackingWorksheetDistanceUnit>,
      speedColumnUnits: {
        [TrackingWorksheetColumnIds.legSpeed]: 'milesPerHour',
        [TrackingWorksheetColumnIds.runAverageSpeed]: 'milesPerHour',
      } as Record<string, TrackingWorksheetSpeedUnit>,
      timeColumnVariants: {
        [TrackingWorksheetColumnIds.passUtc]: 'utc',
        [TrackingWorksheetColumnIds.etaUtc]: 'utc',
      } as Record<string, TrackingWorksheetTimeZone>,
      activeWorksheetColumnVariantMenu: null as string | null,
      distanceUnitOptions: DISTANCE_UNIT_OPTIONS,
      speedUnitOptions: SPEED_UNIT_OPTIONS,
      columnOrder: [] as string[],
      draggedWorksheetColumn: null as string | null,
      worksheetColumnDropTarget: null as null | {
        columnName: string;
        position: ColumnDropPosition;
      },
      hasStoredVisibleColumnsPreference: false,
      hasStoredColumnVariants: false,
      isApplyingDefaultVisibleColumns: false,
      columns: [
        {
          name: TrackingWorksheetColumnIds.marker,
          label: 'Marker',
          field: (row: TrackingWorksheetRow) => row.markerName,
          align: 'left',
          required: true,
          disable: true,
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.status,
          label: 'Status',
          field: (row: TrackingWorksheetRow) => row.status,
          align: 'left',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.linkedAgms,
          label: 'Manually Linked AGMs',
          field: (row: TrackingWorksheetRow) => row.linkedUids.join(', '),
          align: 'left',
          sortable: false,
        },
        {
          name: TrackingWorksheetColumnIds.passSource,
          label: 'Pass Source',
          field: (row: TrackingWorksheetRow) =>
            this.formatPassSource(row.passSource),
          align: 'left',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.passUtc,
          label: 'Pass Time (UTC)',
          field: (row: TrackingWorksheetRow) =>
            isValidDate(row.passageTimeUtc)
              ? row.passageTimeUtc.getTime()
              : null,
          format: (_value: unknown, row: TrackingWorksheetRow) =>
            this.formatTime(
              row.passageTimeUtc,
              TrackingWorksheetColumnIds.passUtc
            ),
          align: 'left',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.etaUtc,
          label: 'ETA (UTC)',
          field: (row: TrackingWorksheetRow) =>
            isValidDate(row.etaUtc) ? row.etaUtc.getTime() : null,
          format: (_value: unknown, row: TrackingWorksheetRow) =>
            this.formatTime(row.etaUtc, TrackingWorksheetColumnIds.etaUtc),
          align: 'left',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.countdown,
          label: 'ETA In',
          field: (row: TrackingWorksheetRow) => row.countdownSeconds,
          format: (value: number | null, row: TrackingWorksheetRow) =>
            this.isOverdue(row.overdueSeconds)
              ? `Overdue ${this.formatCountdown(row.overdueSeconds)}`
              : this.formatCountdown(value),
          align: 'right',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.legSpeed,
          label: 'Leg Speed (m/s)',
          field: (row: TrackingWorksheetRow) => row.legSpeedMetersPerSecond,
          format: (value: number | null, row: TrackingWorksheetRow) =>
            this.formatLegSpeed(value, row),
          align: 'right',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.trackState,
          label: 'Track State',
          field: (row: TrackingWorksheetRow) =>
            formatTrackingWorksheetTrackState(row.trackState),
          align: 'left',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.legDistance,
          label: 'Leg Distance (m)',
          field: (row: TrackingWorksheetRow) => row.legDistanceMeters,
          format: (value: number | null) =>
            this.formatDistance(value, TrackingWorksheetColumnIds.legDistance),
          align: 'right',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.distanceFromLaunch,
          label: 'Cumulative GPS Distance (m)',
          field: (row: TrackingWorksheetRow) => row.distanceFromLaunchMeters,
          format: (value: number | null) =>
            this.formatDistance(
              value,
              TrackingWorksheetColumnIds.distanceFromLaunch
            ),
          align: 'right',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.runAverageSpeed,
          label: 'Run Avg. Speed (m/s)',
          field: (row: TrackingWorksheetRow) =>
            row.runAverageSpeedMetersPerSecond,
          format: (value: number | null) =>
            this.formatSpeed(value, TrackingWorksheetColumnIds.runAverageSpeed),
          align: 'right',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.latitude,
          label: 'Latitude',
          field: (row: TrackingWorksheetRow) => row.latitude,
          format: (value: number | null) => this.formatCoordinate(value),
          align: 'right',
          sortable: true,
        },
        {
          name: TrackingWorksheetColumnIds.longitude,
          label: 'Longitude',
          field: (row: TrackingWorksheetRow) => row.longitude,
          format: (value: number | null) => this.formatCoordinate(value),
          align: 'right',
          sortable: true,
        },
      ] as QTableColumn<TrackingWorksheetRow>[],
    };
  },
  computed: {
    isCompletedRun(): boolean {
      return this.completedPigRunId !== null;
    },
    pigRunId(): number | null {
      return this.isCompletedRun
        ? this.completedPigRunId
        : this.entry?.pigRun.pigRunId ?? null;
    },
    worksheetEntry(): PigRunMapEntry | null {
      if (this.isCompletedRun) {
        return this.completedPigRun
          ? buildCompletedPigRunEntry(
              this.completedPigRun,
              this.completedVerifiedPassageSources,
              this.completedSourceLoadState,
              this.completedSourceLoadError
            )
          : null;
      }
      return this.entry;
    },
    /**
     * Done or Cancelled: the header shows the ended-run summary, like the CSV
     * export (EN-1495 review round 2, R2-9).
     */
    isEndedRun(): boolean {
      return isEndedLifecycleState(this.worksheet?.summary.lifecycleState);
    },
    worksheet(): TrackingWorksheet | null {
      const worksheet = this.worksheetEntry
        ? buildTrackingWorksheet(this.worksheetEntry, this.clock)
        : null;
      if (!worksheet || !this.completedPigRun) return worksheet;

      return {
        ...worksheet,
        summary: {
          ...worksheet.summary,
          pipelineName:
            this.completedPigRun.pipelineSectionName?.trim() || null,
          receiveTimeUtc: parseUtcDateTime(this.completedPigRun.receiveDate),
          deviceCount: this.completedPigRun.devices?.length ?? null,
          passageCount: this.completedPigRun.passageCount ?? null,
          verifiedPassageCount:
            this.completedPigRun.verifiedPassageCount ?? null,
        },
      };
    },
    distanceFromLaunchColumnLabel(): string {
      const label =
        this.worksheet?.distanceFromLaunchSource === 'measured'
          ? 'Pipeline Distance'
          : 'Cumulative GPS Distance';
      return `${label} (${this.distanceUnitSymbol(
        this.distanceUnitForColumn(
          TrackingWorksheetColumnIds.distanceFromLaunch
        )
      )})`;
    },
    primarySpeedUnitSymbol(): string {
      return this.speedUnitSymbol(
        this.speedUnitForColumn(TrackingWorksheetColumnIds.legSpeed)
      );
    },
    worksheetColumns(): QTableColumn<TrackingWorksheetRow>[] {
      const orderedColumns: QTableColumn<TrackingWorksheetRow>[] = [];
      for (const columnName of this.normalizeColumnOrder(this.columnOrder)) {
        const column = this.columns.find(
          (candidate) => candidate.name === columnName
        );
        if (column) {
          orderedColumns.push(column);
        }
      }

      return orderedColumns.map((column) => {
        switch (column.name) {
          case TrackingWorksheetColumnIds.passUtc:
            return {
              ...column,
              label: this.worksheetColumnVariantLabel(
                column.name,
                this.worksheetColumnVariant(column.name)
              ),
              format: (_value: unknown, row: TrackingWorksheetRow) =>
                this.formatTime(row.passageTimeUtc, column.name),
            };
          case TrackingWorksheetColumnIds.etaUtc:
            return {
              ...column,
              label: this.worksheetColumnVariantLabel(
                column.name,
                this.worksheetColumnVariant(column.name)
              ),
              format: (_value: unknown, row: TrackingWorksheetRow) =>
                this.formatTime(row.etaUtc, column.name),
            };
          case TrackingWorksheetColumnIds.legSpeed:
            return {
              ...column,
              label: `Leg Speed (${this.speedUnitSymbol(
                this.speedUnitForColumn(column.name)
              )})`,
            };
          case TrackingWorksheetColumnIds.legDistance:
            return {
              ...column,
              label: `Leg Distance (${this.distanceUnitSymbol(
                this.distanceUnitForColumn(column.name)
              )})`,
            };
          case TrackingWorksheetColumnIds.distanceFromLaunch:
            return {
              ...column,
              label: this.distanceFromLaunchColumnLabel,
            };
          case TrackingWorksheetColumnIds.runAverageSpeed:
            return {
              ...column,
              label: `Run Avg. Speed (${this.speedUnitSymbol(
                this.speedUnitForColumn(column.name)
              )})`,
            };
          default:
            return column;
        }
      });
    },
    sourceLoadState(): string {
      return this.isCompletedRun
        ? this.completedSourceLoadState
        : this.entry?.verifiedPassageSourcesLoadState ?? 'idle';
    },
    sourceLoadError(): string | null {
      return this.isCompletedRun
        ? this.completedSourceLoadError
        : this.entry?.verifiedPassageSourcesError ?? null;
    },
    visibleWorksheetColumns(): string[] {
      return this.visibleColumns;
    },
    completedTrackingWorksheetRefreshVersions(): Record<number, number> {
      return (
        this.$store.state.pigRun.completedTrackingWorksheetRefreshVersions ?? {}
      );
    },
    tracksheetContextKey(): string {
      return this.isCompletedRun
        ? `completed:${this.completedPigRunId}`
        : `active:${this.entry?.pigRun.pigRunId ?? ''}`;
    },
  },
  watch: {
    tracksheetContextKey() {
      this.applyDefaultVisibleColumns();
      this.loadTrackingRun();
    },
    completedTrackingWorksheetRefreshVersions(
      newRefreshVersions: Record<number, number>,
      oldRefreshVersions: Record<number, number>
    ) {
      const pigRunId = this.completedPigRunId;
      if (
        pigRunId !== null &&
        newRefreshVersions[pigRunId] !== oldRefreshVersions[pigRunId]
      ) {
        void this.loadCompletedPigRun();
      }
    },
    visibleColumns(value: string[]) {
      if (this.isApplyingDefaultVisibleColumns) {
        this.isApplyingDefaultVisibleColumns = false;
        return;
      }

      this.hasStoredVisibleColumnsPreference = true;
      const normalizedColumns = this.normalizeVisibleColumns(value);
      if (normalizedColumns.join('|') !== value.join('|')) {
        this.visibleColumns = normalizedColumns;
        return;
      }
      this.persistVisibleColumns(normalizedColumns);
    },
  },
  mounted() {
    this.restoreColumnUnits();
    this.restoreColumnVariants();
    this.restoreColumnOrder();
    if (!this.restoreVisibleColumns()) {
      this.applyDefaultVisibleColumns();
    }
    this.loadTrackingRun();
    this.clockInterval = setInterval(() => {
      this.clock = new Date();
    }, 1000);
  },
  beforeUnmount() {
    this.completedRunRequestVersion += 1;
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
  },
  methods: {
    resetCompletedRunData(): void {
      this.completedPigRun = null;
      this.completedRunLoadState = 'idle';
      this.completedRunLoadError = null;
      this.completedVerifiedPassageSources = [];
      this.completedSourceLoadState = 'idle';
      this.completedSourceLoadError = null;
    },
    loadTrackingRun(): void {
      if (this.isCompletedRun) {
        void this.loadCompletedPigRun();
        return;
      }

      this.completedRunRequestVersion += 1;
      this.resetCompletedRunData();
      if (this.pigRunId !== null) {
        this.$store.dispatch('refreshSinglePigRun', {
          pigRunId: this.pigRunId,
          force: true,
        });
      }
      this.loadVerifiedPassageSources();
    },
    async loadCompletedPigRun(): Promise<void> {
      const pigRunId = this.completedPigRunId;
      if (pigRunId === null) return;

      const requestVersion = ++this.completedRunRequestVersion;
      this.resetCompletedRunData();
      this.completedRunLoadState = 'loading';
      try {
        const response = await PigRunService.getPigRun(pigRunId);
        if (!this.isCurrentCompletedRunRequest(pigRunId, requestVersion)) {
          return;
        }

        const pigRun = response.data as PigRunSummaryData;
        if (pigRun.lifecycleState !== 'D') {
          this.completedRunLoadState = 'error';
          this.completedRunLoadError = 'Selected run is not completed.';
          return;
        }

        this.completedPigRun = pigRun;
        this.completedRunLoadState = 'loaded';
        void this.loadCompletedVerifiedPassageSources(true);
      } catch (error) {
        if (!this.isCurrentCompletedRunRequest(pigRunId, requestVersion)) {
          return;
        }
        console.error(`Failed to load completed pig run ${pigRunId}:`, error);
        this.completedRunLoadState = 'error';
        this.completedRunLoadError = 'Unable to load completed tracksheet.';
      }
    },
    isCurrentCompletedRunRequest(
      pigRunId: number,
      requestVersion: number
    ): boolean {
      return (
        this.completedPigRunId === pigRunId &&
        this.completedRunRequestVersion === requestVersion
      );
    },
    async loadCompletedVerifiedPassageSources(force = false): Promise<void> {
      const pigRunId = this.completedPigRunId;
      if (pigRunId === null || this.completedPigRun === null) return;
      if (!force && this.completedSourceLoadState === 'loaded') return;

      const requestVersion = this.completedRunRequestVersion;
      this.completedSourceLoadState = 'loading';
      this.completedSourceLoadError = null;
      try {
        const response = await PigRunService.getVerifiedPassageSources(
          pigRunId
        );
        if (!this.isCurrentCompletedRunRequest(pigRunId, requestVersion)) {
          return;
        }
        this.completedVerifiedPassageSources = response.data;
        this.completedSourceLoadState = 'loaded';
      } catch (error) {
        if (!this.isCurrentCompletedRunRequest(pigRunId, requestVersion)) {
          return;
        }
        console.error(
          `Failed to load verified passage sources for completed pig run ${pigRunId}:`,
          error
        );
        this.completedSourceLoadState = 'error';
        this.completedSourceLoadError =
          'Unable to load verified passage source details.';
      }
    },
    loadVerifiedPassageSources(force = false): void {
      if (this.isCompletedRun) {
        void this.loadCompletedVerifiedPassageSources(force);
        return;
      }
      if (this.pigRunId === null) return;
      this.$store.dispatch('loadVerifiedPassageSources', {
        pigRunId: this.pigRunId,
        force,
      });
    },
    downloadCsv(): void {
      if (this.worksheet) {
        downloadTrackingWorksheetCsv(this.worksheet);
      }
    },
    onWorksheetRowClick(_event: Event, row: TrackingWorksheetRow): void {
      this.showMarkerOnMap(row);
    },
    showMarkerOnMap(row: TrackingWorksheetRow): void {
      if (
        this.pigRunId === null ||
        row.latitude === null ||
        row.longitude === null
      )
        return;
      this.$emit('show-marker-on-map', {
        pigRunId: this.pigRunId,
        pipelineSectionMarkerId: row.pipelineSectionMarkerId,
        name: row.markerName,
        lat: row.latitude,
        lon: row.longitude,
        trackState: row.trackState,
        description: row.description,
      });
    },
    formatUtc(date: Date | null): string {
      return isValidDate(date)
        ? date
            .toISOString()
            .replace('T', ' ')
            .replace(/\.\d{3}Z$/, ' UTC')
        : '';
    },
    formatBrowserLocal(date: Date | null): string {
      return isValidDate(date) ? date.toLocaleString() : '';
    },
    formatTime(date: Date | null, columnName: string): string {
      return this.timeZoneForColumn(columnName) === 'local'
        ? this.formatBrowserLocal(date)
        : this.formatUtc(date);
    },
    formatPassSource(source: TrackingWorksheetPassSource | null): string {
      if (!source) return '';
      return source.kind === 'manual' ? 'Manual' : `AGM ${source.uid}`;
    },
    formatMeasurement(value: number | null, maximumFractionDigits = 1): string {
      return value === null || !Number.isFinite(value)
        ? ''
        : new Intl.NumberFormat(undefined, {
            maximumFractionDigits,
          }).format(value);
    },
    formatPrimarySpeed(value: number | null): string {
      return this.formatSpeed(value, TrackingWorksheetColumnIds.legSpeed);
    },
    formatDistance(value: number | null, columnName: string): string {
      if (value === null || !Number.isFinite(value)) return '';

      switch (this.distanceUnitForColumn(columnName)) {
        case 'feet':
          return this.formatMeasurement(value / METERS_PER_FOOT);
        case 'miles':
          return this.formatMeasurement(value / METERS_PER_MILE, 3);
        default:
          return this.formatMeasurement(value);
      }
    },
    /**
     * Leg speed, with "(ignored)" for a verified leg PigRunManager does not
     * use for the current speed (EN-1495 review round 2, R2-7).
     */
    formatLegSpeed(value: number | null, row: TrackingWorksheetRow): string {
      const formatted = this.formatSpeed(
        value,
        TrackingWorksheetColumnIds.legSpeed
      );
      if (!row.legSpeedRejection) return formatted;
      return formatted ? `${formatted} (ignored)` : 'Ignored';
    },
    formatSpeed(value: number | null, columnName: string): string {
      if (value === null || !Number.isFinite(value)) return '';

      switch (this.speedUnitForColumn(columnName)) {
        case 'milesPerHour':
          return this.formatMeasurement(
            value * MILES_PER_HOUR_PER_METER_PER_SECOND,
            2
          );
        case 'kilometersPerHour':
          return this.formatMeasurement(
            value * KILOMETERS_PER_HOUR_PER_METER_PER_SECOND,
            2
          );
        default:
          return this.formatMeasurement(value, 2);
      }
    },
    isDistanceWorksheetColumn(columnName: string): boolean {
      return DISTANCE_COLUMN_IDS.includes(
        columnName as (typeof DISTANCE_COLUMN_IDS)[number]
      );
    },
    isSpeedWorksheetColumn(columnName: string): boolean {
      return SPEED_COLUMN_IDS.includes(
        columnName as (typeof SPEED_COLUMN_IDS)[number]
      );
    },
    hasWorksheetColumnUnitVariants(columnName: string): boolean {
      return (
        this.isDistanceWorksheetColumn(columnName) ||
        this.isSpeedWorksheetColumn(columnName)
      );
    },
    distanceUnitForColumn(columnName: string): TrackingWorksheetDistanceUnit {
      return this.distanceColumnUnits[columnName] ?? 'meters';
    },
    speedUnitForColumn(columnName: string): TrackingWorksheetSpeedUnit {
      return this.speedColumnUnits[columnName] ?? 'milesPerHour';
    },
    distanceUnitSymbol(unit: TrackingWorksheetDistanceUnit): string {
      switch (unit) {
        case 'feet':
          return 'ft';
        case 'miles':
          return 'mi';
        default:
          return 'm';
      }
    },
    speedUnitSymbol(unit: TrackingWorksheetSpeedUnit): string {
      switch (unit) {
        case 'milesPerHour':
          return 'mph';
        case 'kilometersPerHour':
          return 'km/h';
        default:
          return 'm/s';
      }
    },
    worksheetColumnUnitOptions(columnName: string) {
      if (this.isDistanceWorksheetColumn(columnName)) {
        return this.distanceUnitOptions;
      }
      if (this.isSpeedWorksheetColumn(columnName)) {
        return this.speedUnitOptions;
      }
      return [];
    },
    worksheetColumnUnit(columnName: string): string {
      return this.isDistanceWorksheetColumn(columnName)
        ? this.distanceUnitForColumn(columnName)
        : this.speedUnitForColumn(columnName);
    },
    worksheetColumnUnitLabel(columnName: string, unit: string): string {
      if (
        this.isDistanceWorksheetColumn(columnName) &&
        isTrackingWorksheetDistanceUnit(unit)
      ) {
        const label =
          columnName === TrackingWorksheetColumnIds.distanceFromLaunch
            ? this.worksheet?.distanceFromLaunchSource === 'measured'
              ? 'Pipeline Distance'
              : 'Cumulative GPS Distance'
            : 'Leg Distance';
        return `${label} (${this.distanceUnitSymbol(unit)})`;
      }
      if (
        this.isSpeedWorksheetColumn(columnName) &&
        isTrackingWorksheetSpeedUnit(unit)
      ) {
        const label =
          columnName === TrackingWorksheetColumnIds.legSpeed
            ? 'Leg Speed'
            : 'Run Avg. Speed';
        return `${label} (${this.speedUnitSymbol(unit)})`;
      }
      return '';
    },
    setWorksheetColumnUnit(columnName: string, unit: string): void {
      if (
        this.isDistanceWorksheetColumn(columnName) &&
        isTrackingWorksheetDistanceUnit(unit)
      ) {
        this.distanceColumnUnits = {
          ...this.distanceColumnUnits,
          [columnName]: unit,
        };
      } else if (
        this.isSpeedWorksheetColumn(columnName) &&
        isTrackingWorksheetSpeedUnit(unit)
      ) {
        this.speedColumnUnits = {
          ...this.speedColumnUnits,
          [columnName]: unit,
        };
      } else {
        return;
      }

      this.activeWorksheetColumnVariantMenu = null;
      this.persistColumnUnits();
    },
    isTimeWorksheetColumn(columnName: string): boolean {
      return TIME_COLUMN_IDS.includes(
        columnName as (typeof TIME_COLUMN_IDS)[number]
      );
    },
    hasWorksheetColumnVariants(columnName: string): boolean {
      return (
        this.hasWorksheetColumnUnitVariants(columnName) ||
        this.isTimeWorksheetColumn(columnName)
      );
    },
    timeZoneForColumn(columnName: string): TrackingWorksheetTimeZone {
      return this.timeColumnVariants[columnName] ?? 'utc';
    },
    worksheetColumnVariantOptions(columnName: string) {
      return this.isTimeWorksheetColumn(columnName)
        ? TIME_ZONE_OPTIONS
        : this.worksheetColumnUnitOptions(columnName);
    },
    worksheetColumnVariant(columnName: string): string {
      return this.isTimeWorksheetColumn(columnName)
        ? this.timeZoneForColumn(columnName)
        : this.worksheetColumnUnit(columnName);
    },
    worksheetColumnVariantLabel(columnName: string, variant: string): string {
      if (
        this.isTimeWorksheetColumn(columnName) &&
        isTrackingWorksheetTimeZone(variant)
      ) {
        const label =
          columnName === TrackingWorksheetColumnIds.passUtc
            ? 'Pass Time'
            : 'ETA';
        return `${label} (${variant === 'utc' ? 'UTC' : 'Local'})`;
      }
      return this.worksheetColumnUnitLabel(columnName, variant);
    },
    openWorksheetColumnVariantMenu(columnName: string): void {
      if (this.hasWorksheetColumnVariants(columnName)) {
        this.activeWorksheetColumnVariantMenu = columnName;
      }
    },
    setWorksheetColumnVariantMenuOpen(
      columnName: string,
      isOpen: boolean
    ): void {
      this.activeWorksheetColumnVariantMenu = isOpen ? columnName : null;
    },
    setWorksheetColumnVariant(columnName: string, variant: string): void {
      if (
        this.isTimeWorksheetColumn(columnName) &&
        isTrackingWorksheetTimeZone(variant)
      ) {
        this.timeColumnVariants = {
          ...this.timeColumnVariants,
          [columnName]: variant,
        };
        this.activeWorksheetColumnVariantMenu = null;
        this.persistColumnVariants();
        return;
      }

      this.setWorksheetColumnUnit(columnName, variant);
    },
    formatCoordinate(value: number | null): string {
      return value === null || !Number.isFinite(value) ? '' : value.toFixed(6);
    },
    formatCountdown(value: number | null): string {
      if (value === null || !Number.isFinite(value)) return '';
      const hours = Math.floor(value / 3600);
      const minutes = Math.floor((value % 3600) / 60);
      const seconds = Math.floor(value % 60);
      if (hours > 0) return `${hours}h ${minutes}m`;
      if (minutes > 0) return `${minutes}m ${seconds}s`;
      return `${seconds}s`;
    },
    statusColor(status: TrackingWorksheetRowStatus): string {
      switch (status) {
        case 'passed':
          return 'positive';
        case 'next':
          return 'primary';
        case 'pending':
          return 'grey-7';
        case 'not-verified':
          return 'grey-7';
        case 'skipped':
          return 'grey-5';
      }
    },
    statusLabel(status: TrackingWorksheetRowStatus): string {
      if (status === 'not-verified') return 'Not verified';
      return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    },
    // EN-1495: a Skipped Receive that is the summary Next is shown as Next too,
    // so the table agrees with the summary.
    rowStatusColor(row: TrackingWorksheetRow): string {
      return row.isSummaryNext
        ? this.statusColor('next')
        : this.statusColor(row.status);
    },
    rowStatusLabel(row: TrackingWorksheetRow): string {
      if (row.isSummaryNext && row.status === 'skipped') return 'Next (Skip)';
      return this.statusLabel(row.status);
    },
    isOverdue(overdueSeconds: number | null): boolean {
      return overdueSeconds !== null && overdueSeconds > 0;
    },
    normalizeColumnOrder(columnOrder: string[]): string[] {
      const allowedColumns = this.columns.map((column) => column.name);
      const allowedColumnSet = new Set(allowedColumns);
      const seenColumns = new Set<string>();
      const validColumns: string[] = [];
      for (const column of columnOrder) {
        const normalizedColumn = this.normalizeWorksheetColumnName(column);
        if (
          !allowedColumnSet.has(normalizedColumn) ||
          seenColumns.has(normalizedColumn)
        ) {
          continue;
        }
        seenColumns.add(normalizedColumn);
        validColumns.push(normalizedColumn);
      }

      return [
        ...validColumns,
        ...allowedColumns.filter((column) => !seenColumns.has(column)),
      ];
    },
    normalizeWorksheetColumnName(columnName: string): string {
      switch (columnName) {
        case TrackingWorksheetColumnIds.passLocal:
          return TrackingWorksheetColumnIds.passUtc;
        case TrackingWorksheetColumnIds.etaLocal:
          return TrackingWorksheetColumnIds.etaUtc;
        default:
          return columnName;
      }
    },
    defaultWorksheetColumnOrder(): string[] {
      return this.columns.map((column) => column.name);
    },
    isWorksheetColumnOrderDefault(): boolean {
      const defaultColumnOrder = this.defaultWorksheetColumnOrder();
      const currentColumnOrder = this.normalizeColumnOrder(this.columnOrder);
      return currentColumnOrder.every(
        (column, index) => column === defaultColumnOrder[index]
      );
    },
    resetWorksheetColumnOrder(): void {
      if (this.isWorksheetColumnOrderDefault()) return;

      this.columnOrder = this.defaultWorksheetColumnOrder();
      try {
        window.localStorage.removeItem(COLUMN_ORDER_STORAGE_KEY);
      } catch {
        // Column persistence is optional when browser storage is unavailable.
      }
    },
    restoreColumnUnits(): void {
      try {
        const savedColumnUnits = window.localStorage.getItem(
          COLUMN_UNIT_STORAGE_KEY
        );
        if (savedColumnUnits) {
          const parsedUnits: unknown = JSON.parse(savedColumnUnits);
          if (typeof parsedUnits !== 'object' || parsedUnits === null) return;

          const units = parsedUnits as {
            distance?: Record<string, unknown>;
            speed?: Record<string, unknown>;
          };
          for (const columnName of DISTANCE_COLUMN_IDS) {
            const unit = units.distance?.[columnName];
            if (isTrackingWorksheetDistanceUnit(unit)) {
              this.distanceColumnUnits[columnName] = unit;
            }
          }
          for (const columnName of SPEED_COLUMN_IDS) {
            const unit = units.speed?.[columnName];
            if (isTrackingWorksheetSpeedUnit(unit)) {
              this.speedColumnUnits[columnName] = unit;
            }
          }
          return;
        }

        const legacyDistanceUnit = window.localStorage.getItem(
          LEGACY_DISTANCE_UNIT_STORAGE_KEY
        );
        const legacySpeedUnit = window.localStorage.getItem(
          LEGACY_SPEED_UNIT_STORAGE_KEY
        );
        if (isTrackingWorksheetDistanceUnit(legacyDistanceUnit)) {
          for (const columnName of DISTANCE_COLUMN_IDS) {
            this.distanceColumnUnits[columnName] = legacyDistanceUnit;
          }
        }
        if (isTrackingWorksheetSpeedUnit(legacySpeedUnit)) {
          for (const columnName of SPEED_COLUMN_IDS) {
            this.speedColumnUnits[columnName] = legacySpeedUnit;
          }
        }
        if (legacyDistanceUnit || legacySpeedUnit) {
          this.persistColumnUnits();
          window.localStorage.removeItem(LEGACY_DISTANCE_UNIT_STORAGE_KEY);
          window.localStorage.removeItem(LEGACY_SPEED_UNIT_STORAGE_KEY);
        }
      } catch {
        // Column-unit persistence is optional when browser storage is unavailable.
      }
    },
    restoreColumnVariants(): void {
      try {
        const savedVariants = window.localStorage.getItem(
          COLUMN_VARIANT_STORAGE_KEY
        );
        if (!savedVariants) return;

        const parsedVariants: unknown = JSON.parse(savedVariants);
        if (typeof parsedVariants !== 'object' || parsedVariants === null) {
          return;
        }

        const variants = parsedVariants as Record<string, unknown>;
        const restoredTimeColumnVariants = { ...this.timeColumnVariants };
        let hasStoredVariant = false;
        for (const columnName of TIME_COLUMN_IDS) {
          const variant = variants[columnName];
          if (isTrackingWorksheetTimeZone(variant)) {
            restoredTimeColumnVariants[columnName] = variant;
            hasStoredVariant = true;
          }
        }
        if (hasStoredVariant) {
          this.timeColumnVariants = restoredTimeColumnVariants;
          this.hasStoredColumnVariants = true;
        }
      } catch {
        // Column-variant persistence is optional when browser storage is unavailable.
      }
    },
    restoreTimeColumnVariantsFromLegacyVisibleColumns(columns: string[]): void {
      if (this.hasStoredColumnVariants) return;

      const restoredTimeColumnVariants = { ...this.timeColumnVariants };
      let hasLegacyLocalVariant = false;
      if (
        columns.includes(TrackingWorksheetColumnIds.passLocal) &&
        !columns.includes(TrackingWorksheetColumnIds.passUtc)
      ) {
        restoredTimeColumnVariants[TrackingWorksheetColumnIds.passUtc] =
          'local';
        hasLegacyLocalVariant = true;
      }
      if (
        columns.includes(TrackingWorksheetColumnIds.etaLocal) &&
        !columns.includes(TrackingWorksheetColumnIds.etaUtc)
      ) {
        restoredTimeColumnVariants[TrackingWorksheetColumnIds.etaUtc] = 'local';
        hasLegacyLocalVariant = true;
      }

      if (hasLegacyLocalVariant) {
        this.timeColumnVariants = restoredTimeColumnVariants;
        this.hasStoredColumnVariants = true;
        this.persistColumnVariants();
      }
    },
    normalizeVisibleColumns(columns: string[]): string[] {
      const allowedColumns = new Set(this.columns.map((column) => column.name));
      const selectedColumns = new Set<string>();
      const normalizedColumns: string[] = [];
      for (const column of columns) {
        const normalizedColumn = this.normalizeWorksheetColumnName(column);
        if (
          allowedColumns.has(normalizedColumn) &&
          !selectedColumns.has(normalizedColumn)
        ) {
          selectedColumns.add(normalizedColumn);
          normalizedColumns.push(normalizedColumn);
        }
      }
      if (!selectedColumns.has(TrackingWorksheetColumnIds.marker)) {
        normalizedColumns.unshift(TrackingWorksheetColumnIds.marker);
      }
      return normalizedColumns;
    },
    worksheetColumnDragClass(columnName: string): Record<string, boolean> {
      const dropTarget = this.worksheetColumnDropTarget;
      return {
        'tracking-worksheet-column--dragging':
          this.draggedWorksheetColumn === columnName,
        'tracking-worksheet-column--drop-before':
          dropTarget?.columnName === columnName &&
          dropTarget.position === 'before',
        'tracking-worksheet-column--drop-after':
          dropTarget?.columnName === columnName &&
          dropTarget.position === 'after',
      };
    },
    clearWorksheetColumnDropTarget(): void {
      if (this.worksheetColumnDropTarget !== null) {
        this.worksheetColumnDropTarget = null;
      }
    },
    setWorksheetColumnDropTarget(
      columnName: string,
      position: ColumnDropPosition
    ): void {
      const currentTarget = this.worksheetColumnDropTarget;
      if (
        currentTarget?.columnName === columnName &&
        currentTarget.position === position
      ) {
        return;
      }

      this.worksheetColumnDropTarget = { columnName, position };
    },
    startWorksheetColumnDrag(event: DragEvent, columnName: string): void {
      if (!this.columns.some((column) => column.name === columnName)) return;

      this.draggedWorksheetColumn = columnName;
      this.clearWorksheetColumnDropTarget();
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', columnName);
      }
    },
    getDraggedWorksheetColumn(event: DragEvent): string | null {
      const columnName =
        this.draggedWorksheetColumn ||
        event.dataTransfer?.getData('text/plain') ||
        null;
      return this.columns.some((column) => column.name === columnName)
        ? columnName
        : null;
    },
    getWorksheetColumnDropPosition(
      event: DragEvent,
      direction: ColumnDropDirection
    ): ColumnDropPosition {
      const target = event.currentTarget as HTMLElement | null;
      if (!target) return 'before';

      const bounds = target.getBoundingClientRect();
      const midpoint =
        direction === 'horizontal'
          ? bounds.left + bounds.width / 2
          : bounds.top + bounds.height / 2;
      const pointer =
        direction === 'horizontal' ? event.clientX : event.clientY;
      return pointer < midpoint ? 'before' : 'after';
    },
    onWorksheetColumnDragOver(
      event: DragEvent,
      targetColumnName: string,
      direction: ColumnDropDirection
    ): void {
      const draggedColumnName = this.getDraggedWorksheetColumn(event);
      if (!draggedColumnName || draggedColumnName === targetColumnName) {
        this.clearWorksheetColumnDropTarget();
        return;
      }

      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      this.setWorksheetColumnDropTarget(
        targetColumnName,
        this.getWorksheetColumnDropPosition(event, direction)
      );
    },
    onWorksheetColumnDrop(
      event: DragEvent,
      targetColumnName: string,
      direction: ColumnDropDirection
    ): void {
      const draggedColumnName = this.getDraggedWorksheetColumn(event);
      event.preventDefault();
      if (draggedColumnName && draggedColumnName !== targetColumnName) {
        this.moveWorksheetColumn(
          draggedColumnName,
          targetColumnName,
          this.getWorksheetColumnDropPosition(event, direction)
        );
      }
      this.endWorksheetColumnDrag();
    },
    endWorksheetColumnDrag(): void {
      this.draggedWorksheetColumn = null;
      this.clearWorksheetColumnDropTarget();
    },
    moveWorksheetColumn(
      columnName: string,
      targetColumnName: string,
      position: ColumnDropPosition
    ): void {
      const orderedColumns = this.normalizeColumnOrder(this.columnOrder);
      const sourceIndex = orderedColumns.indexOf(columnName);
      const targetIndex = orderedColumns.indexOf(targetColumnName);
      if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
        return;
      }

      orderedColumns.splice(sourceIndex, 1);
      const insertionIndex =
        orderedColumns.indexOf(targetColumnName) +
        (position === 'after' ? 1 : 0);
      orderedColumns.splice(insertionIndex, 0, columnName);
      this.setWorksheetColumnOrder(orderedColumns);
    },
    setWorksheetColumnOrder(columnOrder: string[]): void {
      const normalizedColumnOrder = this.normalizeColumnOrder(columnOrder);
      if (normalizedColumnOrder.join('|') === this.columnOrder.join('|')) {
        return;
      }

      this.columnOrder = normalizedColumnOrder;
      this.persistColumnOrder(normalizedColumnOrder);
    },
    defaultVisibleColumns(): string[] {
      return this.isCompletedRun
        ? [...COMPLETED_DEFAULT_VISIBLE_COLUMNS]
        : [...ACTIVE_DEFAULT_VISIBLE_COLUMNS];
    },
    applyDefaultVisibleColumns(): void {
      if (this.hasStoredVisibleColumnsPreference) return;

      const defaultColumns = this.defaultVisibleColumns();
      if (defaultColumns.join('|') === this.visibleColumns.join('|')) return;

      this.isApplyingDefaultVisibleColumns = true;
      this.visibleColumns = defaultColumns;
    },
    restoreVisibleColumns(): boolean {
      try {
        const savedColumns = window.localStorage.getItem(
          VISIBLE_COLUMNS_STORAGE_KEY
        );
        if (!savedColumns) return false;
        const parsedColumns: unknown = JSON.parse(savedColumns);
        if (Array.isArray(parsedColumns)) {
          const savedVisibleColumns = parsedColumns.filter(
            (column): column is string => typeof column === 'string'
          );
          this.restoreTimeColumnVariantsFromLegacyVisibleColumns(
            savedVisibleColumns
          );
          this.hasStoredVisibleColumnsPreference = true;
          this.visibleColumns =
            this.normalizeVisibleColumns(savedVisibleColumns);
          return true;
        }
      } catch {
        // A blocked or malformed preference should not prevent worksheet use.
      }
      return false;
    },
    restoreColumnOrder(): void {
      try {
        const savedOrder = window.localStorage.getItem(
          COLUMN_ORDER_STORAGE_KEY
        );
        if (!savedOrder) {
          this.columnOrder = this.defaultWorksheetColumnOrder();
          return;
        }

        const parsedOrder: unknown = JSON.parse(savedOrder);
        if (Array.isArray(parsedOrder)) {
          this.columnOrder = this.normalizeColumnOrder(
            parsedOrder.filter(
              (column): column is string => typeof column === 'string'
            )
          );
          return;
        }
      } catch {
        // A blocked or malformed preference should not prevent worksheet use.
      }
      this.columnOrder = this.defaultWorksheetColumnOrder();
    },
    persistVisibleColumns(columns: string[]): void {
      try {
        window.localStorage.setItem(
          VISIBLE_COLUMNS_STORAGE_KEY,
          JSON.stringify(columns)
        );
      } catch {
        // Column persistence is optional when browser storage is unavailable.
      }
    },
    persistColumnOrder(columnOrder: string[]): void {
      try {
        window.localStorage.setItem(
          COLUMN_ORDER_STORAGE_KEY,
          JSON.stringify(columnOrder)
        );
      } catch {
        // Column persistence is optional when browser storage is unavailable.
      }
    },
    persistColumnUnits(): void {
      try {
        window.localStorage.setItem(
          COLUMN_UNIT_STORAGE_KEY,
          JSON.stringify({
            distance: this.distanceColumnUnits,
            speed: this.speedColumnUnits,
          })
        );
      } catch {
        // Column-unit persistence is optional when browser storage is unavailable.
      }
    },
    persistColumnVariants(): void {
      try {
        window.localStorage.setItem(
          COLUMN_VARIANT_STORAGE_KEY,
          JSON.stringify(this.timeColumnVariants)
        );
      } catch {
        // Column-variant persistence is optional when browser storage is unavailable.
      }
    },
  },
});
</script>

<style scoped>
.tracking-worksheet-page {
  min-height: 100%;
}

.tracking-worksheet-content {
  max-width: 100%;
}

.worksheet-metrics {
  min-width: 0;
}

.worksheet-metric-label,
.worksheet-metric-value {
  display: block;
}

.worksheet-metric-label {
  color: #757575;
  font-size: 0.75rem;
}

.worksheet-metric-value {
  color: #263238;
  font-size: 0.875rem;
  white-space: nowrap;
}

.worksheet-metric-value--wrap {
  max-width: 180px;
  overflow-wrap: anywhere;
  white-space: normal;
}

.worksheet-description {
  max-width: 320px;
  white-space: normal;
}

.tracking-worksheet-table :deep(thead tr th) {
  background: #24337a;
  color: #ffffff;
  position: sticky;
  top: 0;
  z-index: 1;
}

.tracking-worksheet-column-header,
.tracking-worksheet-column-option {
  cursor: grab;
  user-select: none;
}

.tracking-worksheet-column-header:active,
.tracking-worksheet-column-option:active {
  cursor: grabbing;
}

.tracking-worksheet-column--dragging {
  opacity: 0.55;
}

.tracking-worksheet-column--drop-before {
  box-shadow: inset 3px 0 #f26921;
}

.tracking-worksheet-column--drop-after {
  box-shadow: inset -3px 0 #f26921;
}

.tracking-worksheet-column-option.tracking-worksheet-column--drop-before {
  box-shadow: inset 0 3px #f26921;
}

.tracking-worksheet-column-option.tracking-worksheet-column--drop-after {
  box-shadow: inset 0 -3px #f26921;
}

.tracking-worksheet-column-order-reset {
  color: #24337a;
}

.tracking-worksheet-column-option__variant-button {
  color: #757575;
}

.tracking-worksheet-column-variant-menu {
  min-width: 220px;
}

.tracking-worksheet-table :deep(tbody tr:hover) {
  background: #f2f5ff;
}

.tracking-worksheet-empty {
  min-height: 100%;
}

@media (max-width: 599px) {
  .tracking-worksheet-content {
    padding: 8px;
  }

  .worksheet-metrics {
    order: 3;
    flex-basis: 100%;
  }
}
</style>
